import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Popover from '@mui/material/Popover';
import type {
  EnumParameter as EnumParameterType,
  ScanServiceParameterOption,
} from '@witmetrics/utils';
import { useToggle } from '@/hooks/useToggle';
import { CaretDownIcon } from '@/icons';
import ParameterCard from '../ParameterCard';
import { buildImagePath, findSelectedOption } from '../utils';
import { classes, getOptionsGridClass } from './utils';

export type EnumParameterProps = {
  parameter: EnumParameterType;
  value: string | null;
  onChange: (value: string) => void;
};

export default function EnumParameter({
  parameter,
  value,
  onChange,
}: EnumParameterProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [open, toggleOpen] = useToggle();

  const selectedOption = findSelectedOption(parameter, value);
  const triggerImg = selectedOption?.imgSrc || parameter.imgSrc;

  const handleSelect = (
    e: React.MouseEvent<HTMLDivElement>,
    option: ScanServiceParameterOption
  ) => {
    e.stopPropagation();
    onChange(option.value);
    toggleOpen(false);
  };

  return (
    <>
      <ParameterCard
        interactive
        description={parameter.description}
        headerStartAdornment={<CaretDownIcon className={classes.caret} />}
        label={parameter.label}
        onClick={() => toggleOpen(true)}>
        <div ref={triggerRef} className={classes.trigger}>
          {triggerImg && (
            <img
              className={classes.triggerImage}
              src={buildImagePath(triggerImg)}
              alt={parameter.label}
            />
          )}
          <span className={classes.triggerLabel}>
            {selectedOption?.label ?? ''}
          </span>
        </div>
      </ParameterCard>
      <Popover
        open={open}
        anchorEl={triggerRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { className: classes.popoverPaper } }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => toggleOpen(false)}>
        <div
          className={twMerge(
            classes.optionsGrid,
            getOptionsGridClass(parameter.options.length)
          )}>
          {parameter.options.map((option) => {
            const isSelected = option.value === value;
            return (
              <div
                key={option.value}
                className={twMerge(
                  classes.option,
                  isSelected ? classes.optionSelected : classes.optionDefault
                )}
                onClick={(e) => handleSelect(e, option)}>
                {option.imgSrc && (
                  <img
                    className={classes.optionImage}
                    src={buildImagePath(option.imgSrc)}
                    alt={option.label}
                  />
                )}
                <span
                  className={twMerge(
                    classes.optionLabel,
                    isSelected && classes.optionLabelSelected
                  )}>
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
